import { useLocation } from "react-router";

import { useTenantPresentation } from "@/hooks/use-tenant-presentation";
import { navGroupForPath } from "@/navigation/sidebar-items";

/**
 * The eyebrow for a page that has a place in the sidebar: `{tenant} · {group}`.
 *
 * One form, derived rather than typed, because the eyebrow is the only thing on
 * the page that says which workspace the reader is in and it had stopped being
 * reliable. Fourteen of the twenty-one admin destinations disagreed with the
 * navigation: some named groups that do not exist ("Academics", "Admin",
 * "Instructor"), some reversed the pair into `Registrar · Enrollment`, one named
 * both groups at once, two were bare nouns, and `/admin/courses` announced
 * "Academic structure" - the name of a different destination.
 *
 * ADR 0010 turned that from untidiness into ambiguity. `Attendance`,
 * `Assignments`, and `Announcements` each exist twice in the admin nav now, once
 * as read-only oversight and once as a teaching surface with write controls.
 * `/admin/attendance` and `/admin/teaching/attendance` are one component and
 * rendered a byte-identical header, so nothing above the content distinguished
 * the page that can mark a roll from the page that cannot.
 *
 * Returns undefined off-nav (the design system page), where the header omits the
 * eyebrow rather than inventing one.
 */
export function useWorkspaceEyebrow(): string | undefined {
  const { pathname } = useLocation();
  const { name: tenantName } = useTenantPresentation();

  const group = navGroupForPath(pathname);
  if (!group?.label) return undefined;
  return `${tenantName} · ${group.label}`;
}
